import { MOTION } from '@/components/common/motion';
import { Check, Minus } from '@phosphor-icons/react';
import type { ChangeEvent, FC, KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent } from 'react';

interface SelectionCheckboxProps {
  checked: boolean;
  /** Draws the dash instead of the tick — the select-all box with only some rows selected. */
  indeterminate?: boolean;
  /**
   * Shown, but not togglable — a choice that is already made for the user.
   *
   * The column chooser uses it for the tool-name column, which is pinned and therefore always
   * on: the row still has to render, because a missing row reads as "this column does not
   * exist" rather than "this column cannot be turned off".
   */
  disabled?: boolean;
  onChange: (checked: boolean) => void;
  /** Required: the box carries no visible label in either the card or the table. */
  'aria-label': string;
}

/**
 * The one selection box in the app.
 *
 * A native `input[type=checkbox]` paints in the platform accent — which on this page was the last
 * blue left once the buttons moved to `ActionButton`. So the box is drawn instead: the input keeps
 * every bit of its behaviour (space to toggle, the indeterminate flag, form semantics) and only
 * loses its appearance, and the mark on top is a Phosphor glyph revealed by `peer-checked`.
 *
 * The mark is `pointer-events-none` so clicks still land on the input underneath it.
 */
export const SelectionCheckbox: FC<SelectionCheckboxProps> = ({
  checked,
  indeterminate = false,
  disabled = false,
  onChange,
  'aria-label': ariaLabel,
}) => {
  /*
   * Both the card and the table row this box sits in open the project when they are clicked or
   * when space is pressed on them. Ticking a project is not opening it, so neither event is let
   * through — the keyboard half matters as much as the mouse half, because space is what toggles
   * a focused checkbox.
   */
  const stopClick = (e: ReactMouseEvent) => e.stopPropagation();
  const stopKey = (e: ReactKeyboardEvent) => e.stopPropagation();

  return (
    <span className="relative inline-flex h-5 w-5 shrink-0 items-center justify-center">
      <input
        type="checkbox"
        checked={checked}
        ref={(input) => {
          if (input) input.indeterminate = indeterminate;
        }}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.checked)}
        onClick={stopClick}
        onKeyDown={stopKey}
        disabled={disabled}
        aria-label={ariaLabel}
        className={`peer h-5 w-5 cursor-pointer appearance-none rounded border border-gray-300 bg-white checked:border-primary checked:bg-primary indeterminate:border-primary indeterminate:bg-primary hover:border-gray-400 disabled:cursor-default disabled:opacity-60 disabled:hover:border-gray-300 ${MOTION} focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2`}
      />
      <span className="pointer-events-none absolute hidden text-white peer-checked:block peer-indeterminate:block">
        {indeterminate ? (
          <Minus size={14} weight="bold" aria-hidden="true" />
        ) : (
          <Check size={14} weight="bold" aria-hidden="true" />
        )}
      </span>
    </span>
  );
};
